import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import type { UserResponseDto } from '@nomiss/sdk';
import {
  changeMyPhone,
  checkPhoneChangeAvailability,
  confirmEmailChange,
  deleteMyAccount,
  requestEmailChange,
  toUserProfileV2,
  updateMyProfile,
  type UpdateMyProfileInput,
} from '@nomiss/services';
import { myProfileQueryKey } from './use-users';

/**
 * After email/phone change the API returns new JWTs + a v1 profile. Seed the
 * v2 profile query under the new access token so Account UI does not flash
 * empty while a background refetch runs.
 */
export function seedMyProfileAfterCredentialChange(
  queryClient: QueryClient,
  accessToken: string,
  profile: UserResponseDto,
): void {
  queryClient.setQueryData(
    [...myProfileQueryKey, accessToken],
    toUserProfileV2(profile),
  );
  void queryClient.invalidateQueries({ queryKey: myProfileQueryKey });
}

export function useUpdateMyProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateMyProfileInput) => updateMyProfile(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: myProfileQueryKey });
    },
  });
}

export function useCheckPhoneChangeAvailability() {
  return useMutation({
    mutationFn: (phone: string) => checkPhoneChangeAvailability(phone),
  });
}

export function useChangeMyPhone() {
  return useMutation({
    mutationFn: (firebaseIdToken: string) => changeMyPhone(firebaseIdToken),
  });
}

export function useRequestEmailChange() {
  return useMutation({
    mutationFn: (newEmail: string) => requestEmailChange(newEmail),
  });
}

export function useConfirmEmailChange() {
  return useMutation({
    mutationFn: (otp: string) => confirmEmailChange(otp),
  });
}

export function useDeleteMyAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => deleteMyAccount(),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: myProfileQueryKey });
    },
  });
}
